import { useEffect, useRef, useState } from "react";
import { Check, FloppyDisk, GlobeHemisphereEast, WhatsappLogo, PaintBrush, X, MapPin, Plus, Trash, Copy, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { MigrationImport } from "../../components/MigrationImport";
import { SupportInbox } from "../../components/SupportInbox";
import { DemoPurge } from "../../components/DemoPurge";
import { SubscriptionCard, CoachPlansCard, BusinessTypeCard } from "../../components/PlatformBilling";
import { StripeConnectCard } from "../../components/StripeConnectCard";

const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Stockholm",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function tzNow(tz) {
  try {
    return new Date().toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit" });
  } catch (_e) {
    return "—";
  }
}

const CURRENCY_OPTIONS = [
  { code: "usd", symbol: "$", label: "US Dollar" },
  { code: "gbp", symbol: "£", label: "British Pound" },
  { code: "eur", symbol: "€", label: "Euro" },
  { code: "cad", symbol: "C$", label: "Canadian Dollar" },
  { code: "aud", symbol: "A$", label: "Australian Dollar" },
  { code: "nzd", symbol: "NZ$", label: "New Zealand Dollar" },
  { code: "chf", symbol: "CHF", label: "Swiss Franc" },
  { code: "sek", symbol: "kr", label: "Swedish Krona" },
  { code: "nok", symbol: "kr", label: "Norwegian Krone" },
  { code: "dkk", symbol: "kr", label: "Danish Krone" },
  { code: "jpy", symbol: "¥", label: "Japanese Yen" },
  { code: "inr", symbol: "₹", label: "Indian Rupee" },
  { code: "brl", symbol: "R$", label: "Brazilian Real" },
  { code: "zar", symbol: "R", label: "South African Rand" },
  { code: "mxn", symbol: "Mex$", label: "Mexican Peso" },
];

export default function TrainerSettings() {
  const { user, refresh } = useAuth();
  const brand = useBrand();
  const [currency, setCurrency] = useState((user?.preferred_currency || "usd").toLowerCase());
  const [saving, setSaving] = useState(false);
  const [tz, setTz] = useState("");
  const [tzInitial, setTzInitial] = useState("");
  const [tzSaving, setTzSaving] = useState(false);
  const [waCfg, setWaCfg] = useState({ enabled: false, mode: "stub", from_number: null });
  const [waContact, setWaContact] = useState("");
  const [waContactDirty, setWaContactDirty] = useState(false);
  const [waContactSaving, setWaContactSaving] = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [waModalTo, setWaModalTo] = useState("");
  const [waModalBody, setWaModalBody] = useState("");
  const [waModalError, setWaModalError] = useState("");
  const [brandName, setBrandName] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [brandLogo, setBrandLogo] = useState(null); // data URL
  const [brandSaving, setBrandSaving] = useState(false);
  const [landingEnabled, setLandingEnabled] = useState(false);
  const [landingSlug, setLandingSlug] = useState("");
  const [landingTagline, setLandingTagline] = useState("");
  const [bizAddress, setBizAddress] = useState("");
  const [discoverable, setDiscoverable] = useState(true);
  const [landingSaving, setLandingSaving] = useState(false);
  const logoInputRef = useRef(null);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  useEffect(() => {
    setBrandName(brand.brand_name || "");
    setBrandColor(brand.brand_accent_color || "");
    setBrandLogo(brand.brand_logo_b64 || null);
  }, [brand.brand_name, brand.brand_accent_color, brand.brand_logo_b64]);
  const [brandReplyEmail, setBrandReplyEmail] = useState("");
  useEffect(() => { setBrandReplyEmail(user?.brand_reply_email || ""); }, [user?.brand_reply_email]);
  const [accountantEmail, setAccountantEmail] = useState("");
  useEffect(() => { setAccountantEmail(user?.accountant_email || ""); }, [user?.accountant_email]);

  useEffect(() => {
    setLandingEnabled(Boolean(user?.landing_page_enabled));
    setLandingSlug(user?.landing_page_slug || "");
    setLandingTagline(user?.landing_page_tagline || "");
    setBizAddress(user?.business_address || "");
    setDiscoverable(user?.discoverable !== false);
  }, [user?.landing_page_enabled, user?.landing_page_slug, user?.landing_page_tagline, user?.business_address, user?.discoverable]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/profile/timezone");
        if (!alive) return;
        const t = data?.timezone || "UTC";
        setTz(t);
        setTzInitial(t);
      } catch (_e) { /* ignore */ }
      try {
        const { data } = await api.get("/whatsapp/config");
        if (alive) setWaCfg(data);
      } catch (_e) { /* ignore */ }
      try {
        const { data } = await api.get("/settings/whatsapp-contact");
        if (alive) setWaContact(data?.number || "");
      } catch (_e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const saveWaContact = async () => {
    setWaContactSaving(true);
    try {
      const { data } = await api.put("/settings/whatsapp-contact", { number: waContact.trim() || null });
      setWaContact(data?.number || "");
      setWaContactDirty(false);
      toast.success(data?.number ? "Enquiry number saved — clients now see the WhatsApp button" : "Enquiry number removed — WhatsApp button hidden");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setWaContactSaving(false);
    }
  };

  const sendWaTest = async () => {
    setWaModalError("");
    const to = (waModalTo || "").trim();
    // E.164 validation: leading +, 8-15 digits.
    if (!/^\+[1-9]\d{7,14}$/.test(to)) {
      setWaModalError("Enter a valid E.164 number (e.g. +15551234567).");
      return;
    }
    setWaTesting(true);
    try {
      const payload = { to_e164: to };
      const trimmedBody = (waModalBody || "").trim();
      if (trimmedBody) payload.body = trimmedBody;
      const { data } = await api.post("/whatsapp/test-send", payload);
      if (data.status === "stub") {
        toast.info("WhatsApp running in stub mode — payload logged, no real send.");
      } else if (data.status === "sent") {
        toast.success(`Sent! Twilio SID: ${data.sid}`);
      } else {
        toast.error(`Send failed: ${data.error || data.status}`);
      }
      setWaModalOpen(false);
      setWaModalBody("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setWaTesting(false);
    }
  };

  const openWaTestModal = () => {
    setWaModalTo("");
    setWaModalBody("");
    setWaModalError("");
    setWaModalOpen(true);
  };

  const onLogoPicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200_000) {
      toast.error("Logo too large (max 200KB). Try a smaller PNG.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setBrandLogo(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setBrandLogo(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const saveBranding = async () => {
    if (!user?.id) return;
    if (brandColor && !/^#([0-9a-fA-F]{3}){1,2}$/.test(brandColor.trim())) {
      toast.error("Accent color must be a hex like #2E5BA8");
      return;
    }
    setBrandSaving(true);
    try {
      await api.patch(`/trainers/${user.id}`, {
        brand_name: brandName || null,
        brand_accent_color: brandColor || null,
        brand_logo_b64: brandLogo,
        brand_reply_email: brandReplyEmail.trim() || null,
        accountant_email: accountantEmail.trim() || null,
      });
      await Promise.all([brand.refresh(), refresh()]);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBrandSaving(false);
    }
  };

  const clearBranding = async () => {
    setBrandName("");
    setBrandColor("");
    setBrandLogo(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
    setBrandSaving(true);
    try {
      await api.patch(`/trainers/${user.id}`, {
        brand_name: null, brand_accent_color: null, brand_logo_b64: null,
      });
      await brand.refresh();
      toast.success("Branding reset to Traina defaults");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBrandSaving(false);
    }
  };

  const saveLanding = async () => {
    setLandingSaving(true);
    try {
      const payload = {
        landing_page_enabled: landingEnabled,
        landing_page_tagline: landingTagline.trim() || null,
        discoverable,
      };
      if (bizAddress.trim() !== (user?.business_address || "")) payload.business_address = bizAddress.trim() || null;
      if (landingSlug.trim()) payload.landing_page_slug = landingSlug.trim().toLowerCase();
      const { data } = await api.patch(`/trainers/${user.id}`, payload);
      // Refresh auth.me so user.landing_* in context updates
      try { await refresh(); } catch (_) { /* ignore */ }
      if (data.landing_page_slug) setLandingSlug(data.landing_page_slug);
      if ("business_address" in payload && payload.business_address && data.business_lat == null) {
        toast.warning("Saved, but we couldn't place that address on the map — try adding the town and postcode");
      }
      toast.success(landingEnabled ? "Landing page is live" : "Landing page disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setLandingSaving(false);
    }
  };

  const copyLandingUrl = async () => {
    if (!landingSlug) return;
    const url = `${window.location.origin}/c/${landingSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch (_) {
      toast.error("Copy failed — long-press the link");
    }
  };

  const saveTz = async (next) => {
    setTzSaving(true);
    try {
      const { data } = await api.put("/profile/timezone", { timezone: next });
      setTz(data.timezone);
      setTzInitial(data.timezone);
      toast.success(`Timezone set to ${data.timezone}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid timezone");
    } finally {
      setTzSaving(false);
    }
  };

  const [vatInput, setVatInput] = useState(String(user?.vat_percent ?? ""));
  const [vatNumber, setVatNumber] = useState(user?.vat_number || "");
  const [vatSaving, setVatSaving] = useState(false);

  useEffect(() => {
    setVatInput(user?.vat_percent != null ? String(user.vat_percent) : "");
    setVatNumber(user?.vat_number || "");
  }, [user?.vat_percent, user?.vat_number]);

  const saveVat = async () => {
    if (!user?.id) return;
    const trimmed = vatInput.trim();
    const trimmedNumber = vatNumber.trim();
    let payload;
    if (trimmed === "") {
      payload = { vat_percent: 0, vat_number: trimmedNumber || null };
    } else {
      const num = Number(trimmed);
      if (Number.isNaN(num) || num < 0 || num > 100) {
        toast.error("VAT must be a number between 0 and 100");
        return;
      }
      payload = { vat_percent: num, vat_number: trimmedNumber || null };
    }
    setVatSaving(true);
    try {
      await api.patch(`/trainers/${user.id}`, payload);
      toast.success(payload.vat_percent > 0 ? `VAT set to ${payload.vat_percent}%` : "VAT disabled");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save VAT");
    } finally {
      setVatSaving(false);
    }
  };

  const save = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await api.patch(`/trainers/${user.id}`, { preferred_currency: currency });
      toast.success("Settings saved");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const dirty = currency !== (user?.preferred_currency || "usd").toLowerCase();

  return (
    <div className="space-y-8" data-testid="trainer-settings">
      <div>
        <div className="tracker text-brand mb-3">[ Settings ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Preferences.</h1>
      </div>

      {/* Client sign-up code — trainer only */}
      {user?.access_code && (
        <AccessCodeSection code={user.access_code} userId={user.id} onRotated={refresh} />
      )}

      {/* Currency picker */}
      <section className="card-flat p-6 sm:p-8 max-w-3xl">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <h2 className="font-display text-2xl mb-1">Default currency</h2>
            <p className="text-sm text-zinc-400 max-w-md">
              Used for new sessions, package per-session values, and calendar exports.
              Existing sessions keep the currency they were created with.
            </p>
          </div>
          <div className="tracker text-brand-light">Currently · {currency.toUpperCase()}</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-6" data-testid="currency-grid">
          {CURRENCY_OPTIONS.map((c) => {
            const active = c.code === currency;
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => setCurrency(c.code)}
                className={`px-3 py-3 border text-left flex items-center gap-3 transition-colors ${
                  active
                    ? "bg-brand/15 border-brand text-white"
                    : "border-white/10 hover:border-white/40 text-zinc-300"
                }`}
                data-testid={`currency-option-${c.code}`}
              >
                <span className="font-display text-xl w-10 shrink-0">{c.symbol}</span>
                <span className="min-w-0">
                  <span className="block tracker text-[10px] text-zinc-500">{c.code.toUpperCase()}</span>
                  <span className="block text-xs truncate">{c.label}</span>
                </span>
                {active && <Check size={14} weight="bold" className="ml-auto text-brand-light" />}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="btn-primary disabled:opacity-50"
            data-testid="settings-save"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving…" : "Save changes"}
          </button>
          {dirty && (
            <span className="tracker text-[#FF6B61] text-[10px]" data-testid="settings-dirty">
              Unsaved changes
            </span>
          )}
        </div>
      </section>

      {/* VAT flat rate — owner only */}
      {user?.is_owner && (
        <section className="card-flat p-6 sm:p-8 max-w-3xl" data-testid="settings-vat">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <h2 className="font-display text-2xl mb-1">VAT / Sales tax</h2>
              <p className="text-sm text-zinc-400 max-w-md">
                Applied on top of every new invoice: monthly plan bills, one-off invoices, and package purchases.
                Leave blank or set to 0 to disable.
              </p>
            </div>
            <div className="tracker text-brand-light">
              Current · {user?.vat_percent ? `${user.vat_percent}%` : "off"}
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-end gap-3">
            <div>
              <label className="tracker text-zinc-500 text-[10px] block mb-2">Rate (%)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={vatInput}
                  onChange={(e) => setVatInput(e.target.value)}
                  placeholder="e.g. 20"
                  className="w-28 px-3 py-2 border bg-transparent"
                  data-testid="vat-input"
                />
                <span className="tracker text-zinc-500">%</span>
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="tracker text-zinc-500 text-[10px] block mb-2">VAT registration number</label>
              <input
                type="text"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder="e.g. GB123456789"
                className="w-full px-3 py-2 border bg-transparent font-mono"
                data-testid="vat-number-input"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Printed on every invoice header — leave blank if not registered.</p>
            </div>
            <button
              type="button"
              onClick={saveVat}
              disabled={vatSaving}
              className="btn-primary disabled:opacity-50"
              data-testid="vat-save"
            >
              <FloppyDisk size={14} weight="bold" />
              {vatSaving ? "Saving…" : "Save VAT"}
            </button>
          </div>
        </section>
      )}

      {/* Timezone picker */}
      <section className="card-flat p-6 sm:p-8 max-w-3xl" data-testid="settings-timezone">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
              <GlobeHemisphereEast size={20} weight="duotone" className="text-brand-light" />
              Timezone
            </h2>
            <p className="text-sm text-zinc-400 max-w-md">
              Used for the calendar view, session reminders, and the ICS feed.
              We auto-detect on each login — change it here only if you train clients in another zone.
            </p>
          </div>
          <div className="tracker text-brand-light text-right" data-testid="tz-current">
            {tz || "UTC"}<br />
            <span className="text-zinc-500 text-[10px]">it&apos;s {tzNow(tz || "UTC")} there now</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          <div>
            <label className="tracker block mb-2">Pick a timezone</label>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="w-full px-4 py-3 border bg-transparent"
              data-testid="tz-select"
            >
              {/* keep selected option visible even if not in common list */}
              {tz && !COMMON_TIMEZONES.includes(tz) && <option value={tz}>{tz}</option>}
              {COMMON_TIMEZONES.map((z) => (
                <option key={z} value={z}>{z}  ·  {tzNow(z)}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="tracker block mb-1">Your browser detected</label>
            <button
              type="button"
              onClick={() => setTz(browserTz)}
              className="btn-ghost justify-between"
              data-testid="tz-use-browser"
            >
              <span className="font-mono text-xs truncate">{browserTz}</span>
              <span className="tracker text-zinc-500 text-[10px]">{tzNow(browserTz)}</span>
            </button>
            <p className="tracker text-zinc-600 text-[10px]">Click to pre-fill, then save below.</p>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            disabled={!tz || tz === tzInitial || tzSaving}
            onClick={() => saveTz(tz)}
            className="btn-primary disabled:opacity-50"
            data-testid="tz-save"
          >
            <FloppyDisk size={14} weight="bold" />
            {tzSaving ? "Saving…" : "Save timezone"}
          </button>
          {tz && tz !== tzInitial && (
            <span className="tracker text-[#FF6B61] text-[10px]">Unsaved change</span>
          )}
        </div>
      </section>

      <LocationsSection />

      <WaiverSection />

      <section className="card-flat p-6" data-testid="branding-section">
        <div className="flex items-center gap-3 mb-4">
          <PaintBrush size={20} weight="duotone" style={{ color: brandColor || "var(--brand)" }} />
          <div>
            <h2 className="font-display text-xl">Your branding</h2>
            <p className="tracker text-zinc-500 text-[10px]">
              Replace the Traina look with your own logo + accent color in client-facing pages, emails and PDF statements.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="tracker text-zinc-500 text-[10px] block mb-2">Brand name <span className="opacity-60">(replaces "Traina")</span></label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. Apex Coaching"
                  maxLength={64}
                  className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
                  data-testid="brand-name-input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="tracker text-zinc-500 text-[10px] block mb-2">Business email <span className="opacity-60">(replies to class emails land here)</span></label>
                <input
                  type="email"
                  value={brandReplyEmail}
                  onChange={(e) => setBrandReplyEmail(e.target.value)}
                  placeholder="hello@yourstudio.com"
                  className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
                  data-testid="brand-reply-email-input"
                />
                <p className="tracker text-[9px] text-zinc-600 mt-1.5">Booking confirmations, waitlist and 24-hour class reminders go out as "{brandName || "Traina"}" with this as the reply-to address.</p>
              </div>

              <div className="md:col-span-2">
                <label className="tracker text-zinc-500 text-[10px] block mb-2">Accountant email <span className="opacity-60">(monthly payments CSV goes here)</span></label>
                <input
                  type="email"
                  value={accountantEmail}
                  onChange={(e) => setAccountantEmail(e.target.value)}
                  placeholder="books@youraccountant.com"
                  className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
                  data-testid="accountant-email-input"
                />
                <p className="tracker text-[9px] text-zinc-600 mt-1.5">Used by "Email to my accountant" on Group → Payments.</p>
              </div>

              <div>
                <label className="tracker text-zinc-500 text-[10px] block mb-2">Accent color (hex)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={brandColor || "var(--brand)"}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer"
                    data-testid="brand-color-picker"
                  />
                  <input
                    type="text"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    placeholder="var(--brand)"
                    maxLength={7}
                    className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:border-white/30 outline-none"
                    data-testid="brand-color-text"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="tracker text-zinc-500 text-[10px] block mb-2">Logo <span className="opacity-60">(PNG with transparency, max 200KB, ~256×256 looks best)</span></label>
              <div className="flex items-center gap-4 flex-wrap">
                {brandLogo ? (
                  <div className="relative">
                    <img src={brandLogo} alt="brand logo preview" className="h-16 w-16 object-contain bg-white/5 border border-white/10 p-2" data-testid="brand-logo-preview" />
                    <button type="button" onClick={removeLogo} className="absolute -top-2 -right-2 bg-zinc-900 border border-white/20 rounded-full p-0.5 hover:border-[#FF6B61]" data-testid="brand-logo-remove" title="Remove logo">
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-16 border border-dashed border-white/15 flex items-center justify-center tracker text-[9px] text-zinc-600">
                    no logo
                  </div>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={onLogoPicked}
                  className="text-xs text-zinc-400 file:bg-white/10 file:border-0 file:px-3 file:py-2 file:text-xs file:tracker file:text-white file:cursor-pointer file:hover:bg-white/20"
                  data-testid="brand-logo-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveBranding}
                disabled={brandSaving}
                className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-50"
                data-testid="brand-save-btn"
                style={brandColor ? { backgroundColor: brandColor, borderColor: brandColor } : undefined}
              >
                <FloppyDisk size={12} weight="fill" /> {brandSaving ? "Saving…" : "Save branding"}
              </button>
              <button
                type="button"
                onClick={clearBranding}
                disabled={brandSaving}
                className="btn-ghost !py-2 !px-4 !text-xs disabled:opacity-50"
                data-testid="brand-reset-btn"
              >
                Reset to Traina
              </button>
            </div>
          </div>

          {/* Live preview pane */}
          <div className="lg:col-span-2" data-testid="brand-preview-pane">
            <div className="tracker text-zinc-500 text-[10px] mb-2 flex items-center justify-between">
              <span>Preview as client</span>
              <span className="opacity-60">live</span>
            </div>
            <div className="border border-white/10 bg-[#0A0A0A] overflow-hidden">
              {/* Mock client portal header */}
              <div className="border-b border-white/10 px-4 py-3 flex items-center gap-3">
                {brandLogo ? (
                  <img src={brandLogo} alt="logo" className="h-7 w-7 object-contain" />
                ) : (
                  <div className="h-7 w-7 border border-white/20 flex items-center justify-center text-[10px] font-display">T</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base leading-none uppercase truncate">
                    {brandName || "TRAINA"}
                  </div>
                  <div className="tracker text-zinc-500 mt-1 text-[9px]">
                    {brandName ? `with ${user?.name || "your coach"}` : "Run your training business with confidence"}
                  </div>
                </div>
              </div>
              {/* Mock dashboard tile + button using accent */}
              <div className="p-4 space-y-3">
                <div className="tracker text-zinc-500 text-[9px]">DASHBOARD</div>
                <div className="border border-white/10 p-3">
                  <div className="tracker text-zinc-500 text-[9px] mb-1">Pot balance</div>
                  <div className="font-display text-2xl" style={brandColor ? { color: brandColor } : { color: "var(--brand-light)" }}>£240.00</div>
                </div>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-[10px] tracker text-white border"
                  style={{ backgroundColor: brandColor || "var(--brand)", borderColor: brandColor || "var(--brand)" }}
                >
                  Top up your pot →
                </button>
                <div className="tracker text-zinc-500 text-[9px] pt-2">UPCOMING SESSION</div>
                <div className="border-l-2 pl-3 py-2" style={{ borderColor: brandColor || "var(--brand)" }}>
                  <div className="font-mono text-[10px]">Wed · 07:00</div>
                  <div className="text-xs font-semibold">60 min · {user?.name || "Coach"}</div>
                </div>
                <div className="text-[9px] tracker text-zinc-600 pt-3 text-center">
                  Powered by {brandName || "Traina"}
                </div>
              </div>
            </div>
            <p className="tracker text-zinc-600 text-[9px] mt-2 leading-relaxed">
              Hit "Save branding" to push this look to all your clients. Reset to Traina anytime.
            </p>
          </div>
        </div>
      </section>

      <section className="card-flat p-6" data-testid="landing-page-section">
        <div className="flex items-center gap-3 mb-4">
          <GlobeHemisphereEast size={20} weight="duotone" className={landingEnabled ? "text-emerald-400" : "text-zinc-500"} />
          <div>
            <h2 className="font-display text-xl">Public landing page</h2>
            <p className="tracker text-zinc-500 text-[10px]">
              A branded "book me" page on Traina, auto-shared in WhatsApp reminders.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="landing-enable-toggle-label">
            <input
              type="checkbox"
              checked={landingEnabled}
              onChange={(e) => setLandingEnabled(e.target.checked)}
              className="h-4 w-4 accent-brand-light"
              data-testid="landing-enable-toggle"
            />
            <span className="text-sm">Publish my landing page</span>
          </label>

          <div>
            <label className="tracker text-zinc-400 text-[10px] block mb-1.5">URL slug</label>
            <div className="flex items-center gap-2">
              <span className="tracker text-zinc-500 text-[11px] shrink-0">{(typeof window !== "undefined" ? window.location.origin : "") + "/c/"}</span>
              <input
                type="text"
                value={landingSlug}
                onChange={(e) => setLandingSlug(e.target.value)}
                placeholder="auto-generated from brand"
                className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
                data-testid="landing-slug-input"
              />
              {landingSlug && (
                <button
                  type="button"
                  onClick={copyLandingUrl}
                  className="btn-ghost !py-1.5 !px-3 !text-[10px] whitespace-nowrap"
                  data-testid="landing-copy-url-btn"
                >
                  Copy URL
                </button>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Lowercase letters, numbers, hyphens. 3–40 chars. Auto-generated if blank when you enable.</p>
          </div>

          <div>
            <label className="tracker text-zinc-400 text-[10px] block mb-1.5">Tagline (optional)</label>
            <input
              type="text"
              value={landingTagline}
              onChange={(e) => setLandingTagline(e.target.value)}
              maxLength={140}
              placeholder="Strength, conditioning &amp; nutrition for busy professionals."
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
              data-testid="landing-tagline-input"
            />
            <p className="text-[10px] text-zinc-500 mt-1">{landingTagline.length}/140</p>
          </div>

          <div>
            <label className="tracker text-zinc-400 text-[10px] block mb-1.5">Business address (for the client Discover map)</label>
            <input
              type="text"
              value={bizAddress}
              onChange={(e) => setBizAddress(e.target.value)}
              maxLength={200}
              placeholder="12 Studio Lane, Manchester M1 2AB"
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
              data-testid="landing-address-input"
            />
            <p className="text-[10px] text-zinc-500 mt-1" data-testid="landing-address-status">
              {user?.business_lat != null ? `Pinned on the map${user?.business_city ? ` · ${user.business_city}` : ""}` : "Include the town and postcode so we can pin you on the map."}
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={discoverable} onChange={(e) => setDiscoverable(e.target.checked)} className="h-4 w-4 accent-brand-light" data-testid="landing-discoverable-toggle" />
            <span className="text-sm">List my business in Discover (clients can find me by search or on the map)</span>
          </label>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={saveLanding}
              disabled={landingSaving}
              className="btn-primary !py-1.5 !px-3 !text-[10px] inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="landing-save-btn"
            >
              <FloppyDisk size={12} weight="fill" /> {landingSaving ? "Saving…" : "Save landing page"}
            </button>
            {landingEnabled && landingSlug && (
              <a
                href={`/c/${landingSlug}`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost !py-1.5 !px-3 !text-[10px]"
                data-testid="landing-preview-link"
              >
                Open landing page ↗
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="card-flat p-6" data-testid="whatsapp-section">
        <div className="flex items-center gap-3 mb-4">
          <WhatsappLogo size={20} weight="duotone" className={waCfg.mode === "live" ? "text-emerald-400" : waCfg.mode === "test" ? "text-brand-light" : "text-amber-400"} />
          <div>
            <h2 className="font-display text-xl">WhatsApp</h2>
            <p className="tracker text-zinc-500 text-[10px]">
              Two-way bot chat + 24h session reminders via Twilio.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-4" data-testid="whatsapp-status">
          {waCfg.mode === "live" && (
            <span className="tracker px-2 py-1 border border-emerald-500/30 text-emerald-400">Live · {waCfg.from_number}</span>
          )}
          {waCfg.mode === "test" && (
            <span className="tracker px-2 py-1 border border-brand-light/40 text-brand-light">Test mode · {waCfg.from_number}</span>
          )}
          {waCfg.mode === "stub" && (
            <span className="tracker px-2 py-1 border border-amber-500/30 text-amber-400">Stub mode — payloads logged, no real sends</span>
          )}
          <button
            type="button"
            onClick={openWaTestModal}
            disabled={waTesting}
            className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-50"
            data-testid="whatsapp-test-send-btn"
          >
            {waTesting ? "Sending…" : "Send test message"}
          </button>
        </div>

        {waCfg.mode === "stub" && (
          <p className="tracker text-zinc-500 text-[10px] leading-relaxed">
            To flip to live, set <span className="font-mono text-zinc-300">TWILIO_ACCOUNT_SID</span>, <span className="font-mono text-zinc-300">TWILIO_AUTH_TOKEN</span>, and <span className="font-mono text-zinc-300">TWILIO_WHATSAPP_FROM</span> in <span className="font-mono text-zinc-300">backend/.env</span>, then configure your Twilio webhook URL to point at <span className="font-mono text-zinc-300">/api/whatsapp/webhook</span>.
          </p>
        )}

        <div className="mt-5 pt-5 border-t border-white/10" data-testid="whatsapp-contact-block">
          <div className="tracker text-zinc-500 text-[10px] mb-1.5">CLIENT ENQUIRIES — YOUR WHATSAPP NUMBER</div>
          <p className="text-xs text-zinc-500 mb-3">
            Add your personal WhatsApp number and every client gets a green WhatsApp button in the corner of their portal that opens a chat with you. No Twilio needed. Leave blank to hide the button.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={waContact}
              onChange={(e) => { setWaContact(e.target.value); setWaContactDirty(true); }}
              placeholder="+447700900123"
              className="flex-1 px-3 py-2 border bg-[#0A0A0A] font-mono text-sm"
              data-testid="whatsapp-contact-input"
            />
            <button
              onClick={saveWaContact}
              disabled={!waContactDirty || waContactSaving}
              className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-40"
              data-testid="whatsapp-contact-save"
            >
              {waContactSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </section>

      <MigrationImport />

      <SupportInbox />

      <BusinessTypeCard />
      <StripeConnectCard />
      <SubscriptionCard />

      <CoachPlansCard />

      <DemoPurge />

      <Dialog open={waModalOpen} onOpenChange={(o) => { if (!waTesting) setWaModalOpen(o); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="whatsapp-test-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Send test WhatsApp</DialogTitle>
            <DialogDescription className="tracker text-zinc-500 text-[10px]">
              Send a one-off message to verify your Twilio integration. In stub mode the payload is logged only — no real send.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="tracker text-zinc-400 text-[10px] block mb-1.5">Recipient (E.164)</label>
              <input
                type="tel"
                value={waModalTo}
                onChange={(e) => { setWaModalTo(e.target.value); if (waModalError) setWaModalError(""); }}
                placeholder="+15551234567"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600"
                data-testid="whatsapp-test-modal-to-input"
                autoFocus
              />
              <p className="text-[10px] text-zinc-500 mt-1">International format: leading <code className="font-mono">+</code> followed by country code + number, no spaces.</p>
            </div>
            <div>
              <label className="tracker text-zinc-400 text-[10px] block mb-1.5">Message (optional)</label>
              <textarea
                value={waModalBody}
                onChange={(e) => setWaModalBody(e.target.value)}
                rows={3}
                placeholder="Leave blank to send the default branded greeting."
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-600 resize-none"
                data-testid="whatsapp-test-modal-body-input"
              />
            </div>
            {waModalError && (
              <p className="text-red-400 text-xs" data-testid="whatsapp-test-modal-error">{waModalError}</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setWaModalOpen(false)}
              disabled={waTesting}
              className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-50"
              data-testid="whatsapp-test-modal-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={sendWaTest}
              disabled={waTesting}
              className="btn-primary !py-1.5 !px-3 !text-[10px] disabled:opacity-50"
              data-testid="whatsapp-test-modal-send-btn"
            >
              {waTesting ? "Sending…" : "Send"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function LocationsSection() {
  const [locations, setLocations] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/locations");
      setLocations(data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.post("/locations", { name: trimmed });
      setName("");
      await load();
      toast.success("Location added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Remove "${loc.name}"? Historical sessions will keep their location label.`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      await load();
      toast.success("Location removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove");
    }
  };

  return (
    <section className="card-flat p-6" data-testid="locations-section">
      <div className="flex items-center gap-3 mb-4">
        <MapPin size={20} weight="duotone" className="text-brand-light" />
        <div>
          <h2 className="font-display text-xl">Session locations</h2>
          <p className="tracker text-zinc-500 text-[10px]">
            Add your studios and remote options so you can tag every booking. Great for busy gyms with multiple rooms or a mix of online + in-person clients.
          </p>
        </div>
      </div>

      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 mb-5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Studio 1, Online, Bobby's home gym"
          maxLength={80}
          className="flex-1 px-3 py-2.5 border bg-[#0A0A0A] text-sm"
          data-testid="location-name-input"
        />
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="location-add"
        >
          <Plus size={14} weight="bold" /> {saving ? "Adding…" : "Add"}
        </button>
      </form>

      {locations.length === 0 ? (
        <div className="tracker text-zinc-600 text-[10px]" data-testid="locations-empty">
          No locations yet — add your first one above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="locations-list">
          {locations.map((loc) => (
            <div
              key={loc.id}
              className="flex items-center justify-between border border-white/10 bg-[#0A0A0A] px-3 py-2.5"
              data-testid={`location-row-${loc.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <MapPin size={14} weight="bold" className="text-zinc-500 shrink-0" />
                <span className="text-sm truncate">{loc.name}</span>
              </div>
              <button
                onClick={() => remove(loc)}
                className="btn-ghost !py-1.5 !px-3 !text-[10px] text-red-400"
                data-testid={`location-remove-${loc.id}`}
              >
                <Trash size={11} weight="bold" /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function AccessCodeSection({ code, userId, onRotated }) {
  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Access code copied");
  };
  const copyLink = () => {
    const url = `${window.location.origin}/register?code=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Sign-up link copied — share it with new clients");
  };
  const rotate = async () => {
    if (!window.confirm("Rotate your access code? The old code stops working immediately. Existing clients keep their accounts.")) return;
    try {
      await api.post(`/trainers/${userId}/rotate-access-code`);
      toast.success("New access code generated");
      onRotated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <section className="card-flat p-6 sm:p-8 max-w-3xl border border-brand/40 bg-brand/5" data-testid="settings-access-code">
      <div className="flex items-center justify-between flex-wrap gap-6">
        <div>
          <div className="tracker text-brand-light mb-2">[ Client sign-up code ]</div>
          <div className="font-display text-3xl tracking-widest font-mono" data-testid="settings-my-access-code">{code}</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            Share this code with new clients — they enter it when signing up and are automatically added to your roster.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={copyCode} className="btn-ghost" data-testid="settings-copy-code">
            <Copy size={14} weight="bold" /> Copy code
          </button>
          <button onClick={copyLink} className="btn-primary" data-testid="settings-copy-link">
            <Copy size={14} weight="bold" /> Copy sign-up link
          </button>
          <button onClick={rotate} className="btn-ghost" title="Rotate code" data-testid="settings-rotate-code">
            <ArrowsClockwise size={14} weight="bold" />
          </button>
        </div>
      </div>
    </section>
  );
}

function WaiverSection() {
  const [text, setText] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/forms/waiver-text");
        setText(data.waiver_text || "");
        setIsDefault(!!data.is_default);
      } catch (_e) {}
    })();
  }, []);

  const save = async () => {
    if (text.trim().length < 20) {
      toast.error("Waiver text is too short");
      return;
    }
    setSaving(true);
    try {
      await api.put("/forms/waiver-text", { waiver_text: text });
      toast.success("Waiver text saved");
      setDirty(false);
      setIsDefault(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card-flat p-6" data-testid="waiver-settings-section">
      <div className="mb-4">
        <h2 className="font-display text-xl">Liability waiver</h2>
        <p className="tracker text-zinc-500 text-[10px]">
          New clients sign this during onboarding. {isDefault ? "Currently using the standard template." : "Using your custom text."}
        </p>
      </div>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        rows={8}
        maxLength={10000}
        className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm"
        data-testid="waiver-text-input"
      />
      <div className="flex justify-end mt-3">
        <button onClick={save} disabled={!dirty || saving} className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-40" data-testid="waiver-text-save">
          <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save waiver"}
        </button>
      </div>
    </section>
  );
}
